import { apiFetch } from './api';

const API_URL = import.meta.env.VITE_API_URL || '';

/**
 * Situação da assinatura. É gravada, não derivada da data: "cancelada" não se
 * deduz do calendário, e uma expirada pode ser renovada com data retroativa.
 * Espelho de Brik.Domain/Enum/SituacaoAssinaturaEnum.cs.
 */
export const SITUACAO = { ativa: 1, expirada: 2, cancelada: 3 } as const;

export const ROTULO_SITUACAO: Record<number, string> = {
    1: 'Ativa',
    2: 'Expirada',
    3: 'Cancelada',
};

export interface Assinatura {
    assinaturaClienteId: number;
    /** O login do assinante no serviço do cliente. É a identidade da linha. */
    identificador: string;
    nome?: string | null;
    /** O plano é um produto do catálogo da empresa, não texto livre. */
    produtoId?: number | null;
    produtoNome?: string | null;
    /** Preço de tabela do produto — a tela usa para sugerir o valor. */
    produtoPreco?: number | null;
    situacao: number;
    expiraEm?: string | null;
    aderiuEm?: string | null;
    ultimoAcessoEm?: string | null;
    valor?: number | null;
    quantidade: number;
    origem?: string | null;
    observacao?: string | null;
    /* O vínculo vem resolvido da API, por join: o painel mostra o contato por
       inteiro, não só o nome. */
    contatoId?: number | null;
    contatoNome?: string | null;
    contatoTelefone?: string | null;
    contatoEmail?: string | null;
    conversationId?: number | null;
    negocioNome?: string | null;
    negocioValor?: number | null;
    negocioStatusId?: number | null;
    dataCriacao: string;
    dataAtualizacao: string;
}

export interface AssinaturaPayload {
    identificador: string;
    nome?: string | null;
    /** O plano é um produto do catálogo da empresa, não texto livre. */
    produtoId?: number | null;
    produtoNome?: string | null;
    /** Preço de tabela do produto — a tela usa para sugerir o valor. */
    produtoPreco?: number | null;
    situacao: number;
    expiraEm?: string | null;
    aderiuEm?: string | null;
    ultimoAcessoEm?: string | null;
    valor?: number | null;
    quantidade: number;
    origem?: string | null;
    observacao?: string | null;
    /** Só quando a assinatura nasce dentro de um negócio: liga as três pontas. */
    conversationId?: number | null;
}

export interface ApiResponse<T> {
    sucesso: boolean;
    mensagem: string;
    dados: T;
}

/** A API responde 400 com corpo quando recusa — a mensagem dela é a que vale. */
const ler = async <T,>(response: Response, erroPadrao: string): Promise<ApiResponse<T>> => {
    let corpo: ApiResponse<T> | null = null;
    try {
        corpo = await response.json();
    } catch {
        corpo = null;
    }
    if (!response.ok) {
        throw new Error(corpo?.mensagem || erroPadrao);
    }
    return corpo as ApiResponse<T>;
};

const JSON_HEADERS = { 'Content-Type': 'application/json' };

export const AssinaturaService = {
    getAll: async (): Promise<ApiResponse<Assinatura[]>> => {
        const response = await apiFetch(`${API_URL}/api/AssinaturaCliente/BuscarTodas`, {
            method: 'GET',
            headers: JSON_HEADERS,
        });
        // Empresa sem nenhuma assinatura é estado normal, não erro.
        if (response.status === 400) {
            return { sucesso: true, mensagem: 'Sem assinaturas', dados: [] };
        }
        return ler<Assinatura[]>(response, 'Erro ao buscar as assinaturas');
    },

    /** A assinatura deste negócio; `dados` nulo quando ainda não há. */
    getByNegocio: async (conversationId: number): Promise<ApiResponse<Assinatura | null>> => {
        const response = await apiFetch(`${API_URL}/api/AssinaturaCliente/BuscarPorNegocio/${conversationId}`, {
            method: 'GET',
            headers: JSON_HEADERS,
        });
        if (response.status === 400) {
            return { sucesso: true, mensagem: 'Sem assinatura', dados: null };
        }
        return ler<Assinatura | null>(response, 'Erro ao buscar a assinatura do negócio');
    },

    create: async (payload: AssinaturaPayload): Promise<ApiResponse<Assinatura>> => {
        const response = await apiFetch(`${API_URL}/api/AssinaturaCliente/Criar`, {
            method: 'POST',
            headers: JSON_HEADERS,
            body: JSON.stringify(payload),
        });
        return ler<Assinatura>(response, 'Erro ao criar a assinatura');
    },

    update: async (id: number, payload: AssinaturaPayload): Promise<ApiResponse<Assinatura>> => {
        const response = await apiFetch(`${API_URL}/api/AssinaturaCliente/Atualizar`, {
            method: 'PATCH',
            headers: JSON_HEADERS,
            body: JSON.stringify({ ...payload, assinaturaClienteId: id }),
        });
        return ler<Assinatura>(response, 'Erro ao atualizar a assinatura');
    },

    remove: async (id: number): Promise<ApiResponse<null>> => {
        const response = await apiFetch(`${API_URL}/api/AssinaturaCliente/Remover/${id}`, {
            method: 'DELETE',
            headers: JSON_HEADERS,
        });
        return ler<null>(response, 'Erro ao remover a assinatura');
    },

    /** `contatoId` nulo desfaz o vínculo — o mesmo endpoint liga e desliga. */
    vincularContato: async (id: number, contatoId: number | null): Promise<ApiResponse<Assinatura>> => {
        const response = await apiFetch(`${API_URL}/api/AssinaturaCliente/VincularContato`, {
            method: 'PATCH',
            headers: JSON_HEADERS,
            body: JSON.stringify({ assinaturaClienteId: id, contatoId }),
        });
        return ler<Assinatura>(response, 'Erro ao vincular o contato');
    },

    /** `conversationId` nulo desfaz o vínculo. */
    vincularNegocio: async (id: number, conversationId: number | null): Promise<ApiResponse<Assinatura>> => {
        const response = await apiFetch(`${API_URL}/api/AssinaturaCliente/VincularNegocio`, {
            method: 'PATCH',
            headers: JSON_HEADERS,
            body: JSON.stringify({ assinaturaClienteId: id, conversationId }),
        });
        return ler<Assinatura>(response, 'Erro ao vincular o negócio');
    },
};

/**
 * A data CIVIL de um ISO, sem passar pelo fuso.
 *
 * Vencimento é dia de calendário, não instante: a API grava `2026-11-25T00:00:00Z`
 * e `new Date(iso)` em UTC-3 devolve 24/11 às 21h — a tela mostrava um dia antes
 * do que a pessoa digitou. Lendo os números do próprio texto, o dia é o dia.
 */
const dataCivil = (iso: string): Date => {
    const [ano, mes, dia] = iso.slice(0, 10).split('-').map(Number);
    return new Date(ano, mes - 1, dia);
};

/** `dd/mm/aaaa` da data civil; '—' quando não há data. */
export const formatarData = (iso?: string | null): string =>
    iso ? dataCivil(iso).toLocaleDateString('pt-BR') : '—';

/** Dias até vencer; negativo já venceu. Nulo quando não há data. */
export const diasAteVencer = (expiraEm?: string | null): number | null => {
    if (!expiraEm) return null;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const alvo = dataCivil(expiraEm);
    return Math.round((alvo.getTime() - hoje.getTime()) / 86400000);
};
